import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Image } from '@udemy-v2/react-core-components';
import classNames from 'classnames';
import { Icon } from '@udemy-v2/icons';
import { useMatchMedia } from '@udemy-v2/design-system-utils';
import { tokens } from '@udemy-v2/styles';
import { noop } from '@udemy-v2/utils';
import { useI18n, Duration } from '@udemy-v2/i18n';
import { useIsCurrentPreview, useSetSelectedPreviewAssetId, useIsUsingPromoVideoFallback, usePreviewableLectures, useSelectedPreviewAsset } from './context/course-preview-video-context';
import { PreviewableLecture } from '@/types/lecture';
import { useClpCourse } from '../../context/clp-course-provider.react-component';
import { MediaPlayerWrapper } from '../media-player/asset-media-player.react-component';
import { publishEvent } from '@udemy-v2/event-tracking';
import { PreviewVideoPlayEvent } from '../../events/events';
import { PreviewVideoViewEvent } from '@/features/course-landing-page/components/course-preview/events';
import styles from './course-preview.module.scss';
const THUMBNAIL_WIDTH = 640;
const THUMBNAIL_HEIGHT = 480;
interface PreviewRowProps {
  /**
   * Video preview to play
   */
  previewableLecture: PreviewableLecture;
  /**
   * Called when the play button is clicked
   */
  onPlay?(): void;
  /**
   * Called when this preview is selected to publish tracking event
   */
  onPreviewSelected?(lectureId: string): void;
}
export const PreviewRow = t0 => {
  const $ = _c(24);
  const {
    previewableLecture,
    onPlay: t1,
    onPreviewSelected
  } = t0;
  const play = t1 === undefined ? noop : t1;
  const isMobile = useMatchMedia(`(max-width: ${tokens["viewport-sm-max"]})`);
  const {
    id,
    title,
    thumbnail,
    durationInSeconds
  } = previewableLecture;
  const showPreviewVideoDuration = !isMobile && durationInSeconds > 0;
  const isCurrentPreview = useIsCurrentPreview(id);
  const setSelectedPreviewAssetId = useSetSelectedPreviewAssetId();
  let t2;
  if ($[0] !== id || $[1] !== onPreviewSelected || $[2] !== play || $[3] !== setSelectedPreviewAssetId) {
    t2 = () => {
      setSelectedPreviewAssetId(id, () => {
        onPreviewSelected?.(id);
      });
      play();
    };
    $[0] = id;
    $[1] = onPreviewSelected;
    $[2] = play;
    $[3] = setSelectedPreviewAssetId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handlePreviewSelection = t2;
  let t3;
  if ($[5] !== isCurrentPreview) {
    t3 = classNames("ud-custom-focus-visible", styles["preview-row"], {
      [styles["current-preview-row"]]: isCurrentPreview
    });
    $[5] = isCurrentPreview;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = thumbnail ?? undefined;
  let t5;
  if ($[7] !== t4) {
    t5 = <div className={styles["preview-thumbnail-wrapper"]}><Image src={t4} alt="" width={THUMBNAIL_WIDTH} height={THUMBNAIL_HEIGHT} className={styles["preview-thumbnail"]} /></div>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isCurrentPreview) {
    t6 = isCurrentPreview && <Icon glyph="play" label={false} color="inherit" className={styles.icon} />;
    $[9] = isCurrentPreview;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = classNames("ud-heading-sm", "ud-focus-visible-target", styles["preview-row-title"]);
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== title) {
    t8 = <div className={t7}>{title}</div>;
    $[12] = title;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== durationInSeconds || $[15] !== showPreviewVideoDuration) {
    t9 = showPreviewVideoDuration && <div className="ud-heading-xs"><Duration numSeconds={durationInSeconds} presentationStyle={Duration.STYLE.TIMESTAMP} /></div>;
    $[14] = durationInSeconds;
    $[15] = showPreviewVideoDuration;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== handlePreviewSelection || $[18] !== t3 || $[19] !== t5 || $[20] !== t6 || $[21] !== t8 || $[22] !== t9) {
    t10 = <button className={t3} onClick={handlePreviewSelection}>{t5}{t6}{t8}{t9}</button>;
    $[17] = handlePreviewSelection;
    $[18] = t3;
    $[19] = t5;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
export interface CoursePreviewProps {
  /**
   * UI region for event tracking
   */
  uiRegion?: string;
  /**
   * Selected lecture ID to start with
   */
  selectedLectureId?: string;
}
export const CoursePreview = t0 => {
  const $ = _c(44);
  const {
    uiRegion,
    selectedLectureId
  } = t0;
  const {
    gettext
  } = useI18n();
  const course = useClpCourse();
  const previewableLectures = usePreviewableLectures();
  const selectedAsset = useSelectedPreviewAsset();
  const setSelectedPreviewAssetId = useSetSelectedPreviewAssetId();
  const isUsingPromoVideoFallback = useIsUsingPromoVideoFallback();
  const hasInitialized = useRef(false);
  let t1;
  if ($[0] !== course.id || $[1] !== previewableLectures || $[2] !== uiRegion) {
    t1 = lectureId => {
      if (!previewableLectures || !uiRegion) {
        return;
      }
      const targetLecture = previewableLectures.find(lecture => lecture.id === lectureId);
      if (targetLecture) {
        const courseIdNum = Number(course.id) || 0;
        const previewIdNum = Number(targetLecture.id) || -1;
        publishEvent(new PreviewVideoViewEvent({
          courseId: courseIdNum,
          buyableTrackingId: course.id,
          previewId: previewIdNum,
          uiRegion
        }));
      }
    };
    $[0] = course.id;
    $[1] = previewableLectures;
    $[2] = uiRegion;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const publishPreviewEvent = t1;
  let t2;
  let t3;
  if ($[4] !== previewableLectures || $[5] !== publishPreviewEvent || $[6] !== selectedLectureId || $[7] !== setSelectedPreviewAssetId) {
    t2 = () => {
      if (previewableLectures && previewableLectures.length > 0 && !hasInitialized.current) {
        const targetLectureId = selectedLectureId || previewableLectures[0].id;
        setSelectedPreviewAssetId(targetLectureId, () => publishPreviewEvent(targetLectureId));
        hasInitialized.current = true;
      }
    };
    t3 = [selectedLectureId, previewableLectures, setSelectedPreviewAssetId, publishPreviewEvent];
    $[4] = previewableLectures;
    $[5] = publishPreviewEvent;
    $[6] = selectedLectureId;
    $[7] = setSelectedPreviewAssetId;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== course.id || $[11] !== isUsingPromoVideoFallback || $[12] !== uiRegion) {
    t4 = () => {
      if (isUsingPromoVideoFallback && uiRegion && !hasInitialized.current) {
        const courseIdNum_0 = Number(course.id) || 0;
        publishEvent(new PreviewVideoViewEvent({
          courseId: courseIdNum_0,
          buyableTrackingId: course.id,
          previewId: -1,
          uiRegion
        }));
        hasInitialized.current = true;
      }
    };
    t5 = [isUsingPromoVideoFallback, course.id, uiRegion];
    $[10] = course.id;
    $[11] = isUsingPromoVideoFallback;
    $[12] = uiRegion;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  if ($[15] !== course.locale) {
    t6 = {
      defaultCaptionLanguage: course.locale
    };
    $[15] = course.locale;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const playerConfig = t6;
  let t7;
  if ($[17] !== course.id || $[18] !== selectedAsset || $[19] !== uiRegion) {
    t7 = currentTime => {
      if (!selectedAsset) {
        return;
      }
      const previewId = selectedAsset.id !== undefined && selectedAsset.id !== "" && !isNaN(Number(selectedAsset.id)) ? Number(selectedAsset.id) : -1;
      publishEvent(new PreviewVideoPlayEvent({
        courseId: Number(course.id),
        buyableTrackingId: course.id,
        previewId,
        offset: Math.floor(currentTime),
        nonInteraction: false,
        uiRegion
      }));
    };
    $[17] = course.id;
    $[18] = selectedAsset;
    $[19] = uiRegion;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handlePlay = t7;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = classNames("ud-heading-sm", styles["preview-intro"]);
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== gettext) {
    t9 = gettext("Course Preview");
    $[22] = gettext;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <span className={t8}>{t9}</span>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== course.title) {
    t11 = <span className="ud-heading-lg">{course.title}</span>;
    $[26] = course.title;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <h2 className={styles["course-preview-modal-title"]}>{t10}{t11}</h2>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const t13 = selectedAsset?.id;
  let t14;
  if ($[31] !== handlePlay || $[32] !== playerConfig || $[33] !== selectedAsset || $[34] !== t13) {
    t14 = <MediaPlayerWrapper key={t13} asset={selectedAsset} onPlay={handlePlay} config={playerConfig} />;
    $[31] = handlePlay;
    $[32] = playerConfig;
    $[33] = selectedAsset;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== gettext || $[37] !== previewableLectures || $[38] !== publishPreviewEvent) {
    t15 = previewableLectures?.length && <><div className={classNames("ud-heading-md", styles["separation-text"])}>{gettext("Free Sample Videos:")}</div><div className={styles["preview-rows"]}>{(previewableLectures ?? []).map(previewableLecture => <PreviewRow previewableLecture={previewableLecture} key={previewableLecture.id} onPreviewSelected={publishPreviewEvent} />)}</div></>;
    $[36] = gettext;
    $[37] = previewableLectures;
    $[38] = publishPreviewEvent;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t12 || $[41] !== t14 || $[42] !== t15) {
    t16 = <div className={styles.previews}>{t12}{t14}{t15}</div>;
    $[40] = t12;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
};